import React, { useState, useEffect, useCallback } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLang } from '../../context/LangContext';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import PageContainerLayout from '../../layout/PageContainerLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import PrimaryButton from '../../components/PrimaryButton';
import Modal from '../../components/Modal';
import ModalFooter from '../../components/ModalFooter';
import { AppTextField } from '../../customComponents/AppTextField';
import { AppGPhoneTextField } from '../../customComponents/AppGPhoneTextField';
import { getUserInfo, updateUserInfo } from '../../lib/api/user/userApi';
import { AppColors } from '../../styles/colors';

// EditProfileContainer styles are now handled by PageContainerLayout

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
  flex: 1;
  
  @media (max-width: 767px) {
    gap: 20px;
    margin-bottom: 24px;
  }
`;

const NoticeText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #666666;
  margin-bottom: 32px;
  text-align: left;
  margin-top: 16px;
  
  @media (max-width: 767px) {
    margin-bottom: 24px;
  }
`;

const ProviderTypeText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #000;
  text-align: left;
  
  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

const ProviderTypeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 34px;
  
  @media (max-width: 767px) {
    margin-bottom: 24px;
  }
`;

const PasswordChangeButton = styled.button`
  width: 110px;
  height: 28px;
  font-size: 12px;
  font-weight: 500;
  padding: 0;
  border: none;
  border-radius: 8px;
  background-color: ${AppColors.btnE};
  color: ${AppColors.onBtnE};
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.2s, color 0.2s;
  
  &:hover {
    background-color: ${AppColors.btnE_Hover};
    color: ${AppColors.onBtnE_Hover};
  }
  
  &:disabled {
    background-color: ${AppColors.btnE_Disabled};
    color: ${AppColors.onBtnE_Disabled};
    cursor: not-allowed;
  }
`;

const WithdrawalButton = styled.button`
  font-size: 15px;
  font-weight: 500;
  color: #949494;
  background: none;
  border: none;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin: 24px 0;
  align-self: center;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ButtonContainer = styled.div`
  margin-top: auto;
  padding-top: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
`;

// 다국어 텍스트 객체
const texts = {
  ko: {
    editProfile: '정보 수정',
    editNotice: '*기본 정보를 제외한 연락처만 수정 가능합니다',
    generalUser: '일반유저',
    kakaoUser: '카카오',
    naverUser: '네이버',
    appleUser: '애플',
    googleUser: '구글',
    changePassword: '비밀번호 변경',
    nameLabel: '이름',
    emailLabel: '이메일',
    cellphoneLabel: '연락처',
    saveChanges: '저장',
    saving: '저장 중...',
    loadingUserInfo: '사용자 정보를 불러오는 중...',
    loadFailed: '사용자 정보를 불러오는데 실패했습니다.',
    saveSuccess: '정보가 성공적으로 수정되었습니다.',
    saveFailed: '정보 수정에 실패했습니다.',
    cellphonePlaceholder: '연락처를 입력해주세요',
    phoneValidationError: '올바른 연락처를 입력해주세요',
    withdrawal: '회원탈퇴',
    withdrawalConfirmTitle: '회원 탈퇴하시겠습니까?',
    withdrawalConfirmMessage: '탈퇴 시 회원정보 및 이용 내역이 모두 삭제되며,\n복구가 불가능합니다.',
    withdrawalConfirm: '탈퇴하기',
    withdrawalCancel: '닫기',
  },
  en: {
    editProfile: 'Edit Profile',
    editNotice: '*Only contact information can be modified except basic information',
    generalUser: 'General User',
    kakaoUser: 'Kakao',
    naverUser: 'Naver',
    appleUser: 'Apple',
    googleUser: 'Google',
    changePassword: 'Change Password',
    nameLabel: 'Name',
    emailLabel: 'Email',
    cellphoneLabel: 'Phone Number',
    saveChanges: 'Save',
    saving: 'Saving...',
    loadingUserInfo: 'Loading user information...',
    loadFailed: 'Failed to load user information.',
    saveSuccess: 'Information updated successfully.',
    saveFailed: 'Failed to update information.',
    cellphonePlaceholder: 'Enter your phone number',
    phoneValidationError: 'Please enter a valid phone number',
    withdrawal: 'Withdraw Membership',
    withdrawalConfirmTitle: 'Do you want to withdraw your membership?',
    withdrawalConfirmMessage: 'All member information and usage history will be deleted upon withdrawal,\nand recovery is not possible.',
    withdrawalConfirm: 'Withdraw',
    withdrawalCancel: 'Close',
  },
};

interface UserInfoData {
  index: number;
  name: string;
  email: string;
  locationType: string;
  providerType: string | null;
  providerId: string | null;
  cellphone: string;
  lastLoginAt: string;
  createAt: string;
  updateAt: string | null;
}

function EditProfile() {
  const navigate = useNavigate();
  const { isLoggedIn, user } = useUserAuth();
  const { lang } = useLang();
  const [userInfo, setUserInfo] = useState<UserInfoData | null>(null);
  const [cellphone, setCellphone] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);

  const t = texts[lang];

  // 프로바이더 타입에 따른 텍스트 반환
  const getProviderTypeText = (providerType: string | null | undefined) => {
    if (!providerType || providerType === '') {
      return t.generalUser;
    }
    
    switch (providerType.toUpperCase()) {
      case 'KAKAO':
        return t.kakaoUser;
      case 'NAVER':
        return t.naverUser;
      case 'APPLE':
        return t.appleUser;
      case 'GOOGLE':
        return t.googleUser;
      default:
        return t.generalUser;
    }
  };

  // 사용자 정보 로드
  useEffect(() => {
    const loadUserInfo = async () => {
      if (!isLoggedIn) {
        navigate('/login');
        return;
      }

      try {
        setIsLoading(true);
        const response = await getUserInfo();
        
        if (response && response.data && response.data.statusCode === 200) {
          const userData = response.data.data;
          setUserInfo(userData);
          setCellphone(userData.cellphone || '');
          setIsPhoneValid(!!userData.cellphone);
        } else {
          toast.error(t.loadFailed);
          navigate('/mypage');
        }
      } catch (error) {
        devError('사용자 정보 로드 실패:', error);
        toast.error(t.loadFailed);
        navigate('/mypage');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserInfo();
  }, [isLoggedIn, navigate, t]);

  const handlePhoneChange = useCallback((value: string, isValid: boolean) => {
    setCellphone(value);
    setIsPhoneValid(isValid);
  }, []);

  const handleSave = useCallback(async () => {
    if (!isPhoneValid || cellphone.trim() === '') {
      toast.error(t.phoneValidationError);
      return;
    }

    try {
      setIsSaving(true);
      
      // 사용자 정보 업데이트 API 호출
      const response = await updateUserInfo({ cellphone: cellphone.trim() });
      
      // 응답 데이터의 statusCode 확인 후 처리
      if (response.data.statusCode === 200) {
        toast.success(t.saveSuccess);
        navigate('/mypage');
      } else {
        // 기타 에러 응답 처리
        const errorMessage = response.data.message || t.saveFailed;
        toast.error(errorMessage);
      }
      
    } catch (error: any) {
      devError('정보 수정 실패:', error);
      
      // 에러 객체에서 메시지 추출
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error?.message) {
        toast.error(error.message);
      } else {
        toast.error(t.saveFailed);
      }
    } finally {
      setIsSaving(false);
    }
  }, [isPhoneValid, cellphone, t, navigate]);

  const handleBack = useCallback(() => {
    navigate('/mypage');
  }, [navigate]);

  const handlePasswordChange = useCallback(() => {
    navigate('/change-password');
  }, [navigate]);

  const handleWithdrawal = useCallback(() => {
    setShowWithdrawalModal(true);
  }, []);

  const handleWithdrawalConfirm = useCallback(() => {
    // TODO: 실제 회원탈퇴 API 호출
    devLog('회원탈퇴 확인');
    setShowWithdrawalModal(false);
    // 탈퇴 후 로그인 페이지로 이동 등의 처리
  }, []);

  const handleWithdrawalCancel = useCallback(() => {
    setShowWithdrawalModal(false);
  }, []);

  if (isLoading) {
    return (
      <MainLayout currentPage="mypage">
        <CenteredLayout>
          <ProjectAppbar 
            currentPage="mypage"
            title={t.editProfile}
            showBackButton={true}
            onBack={handleBack}
          />
          <LoadingContainer>
            {t.loadingUserInfo}
          </LoadingContainer>
        </CenteredLayout>
      </MainLayout>
    );
  }

  if (!userInfo) {
    return null;
  }

  return (
    // <MainLayout currentPage="mypage">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="mypage"
          title={t.editProfile}
          showBackButton={true}
          onBack={handleBack}
        />
        <PageContainerLayout>
          <NoticeText>{t.editNotice}</NoticeText>
          <ProviderTypeRow>
            <ProviderTypeText>{getProviderTypeText(user?.providerType)}</ProviderTypeText>
            {(!user?.providerType || user?.providerType === '') && (
              <PasswordChangeButton onClick={handlePasswordChange}>
                {t.changePassword}
              </PasswordChangeButton>
            )}
          </ProviderTypeRow>
          <FormSection>
            <AppTextField
              value={userInfo.name}
              onChange={() => {}} // readonly이지만 onChange는 필수이므로 빈 함수
              label={t.nameLabel}
              readOnly={true}
            />

            <AppTextField
              value={userInfo.email}
              onChange={() => {}} // readonly이지만 onChange는 필수이므로 빈 함수
              label={t.emailLabel}
              readOnly={true}
            />

            <AppGPhoneTextField
              value={cellphone}
              onChange={handlePhoneChange}
              label={t.cellphoneLabel}
              placeholder={t.cellphonePlaceholder}
              autoComplete="tel"
              language={lang}
            />
          </FormSection>

          <WithdrawalButton onClick={handleWithdrawal}>
            {t.withdrawal}
          </WithdrawalButton>

          <ButtonContainer>
            <PrimaryButton
              onClick={handleSave}
              disabled={!isPhoneValid || cellphone.trim() === '' || isSaving}
            >
              {isSaving ? t.saving : t.saveChanges}
            </PrimaryButton>
          </ButtonContainer>
        </PageContainerLayout>

        <Modal
          isOpen={showWithdrawalModal}
          onClose={handleWithdrawalCancel}
          header={t.withdrawalConfirmTitle}
          body={t.withdrawalConfirmMessage}
          footer={
            <ModalFooter
              leftButtonText={t.withdrawalCancel}
              rightButtonText={t.withdrawalConfirm}
              onLeftClick={handleWithdrawalCancel}
              onRightClick={handleWithdrawalConfirm}
            />
          }
        />
      </CenteredLayout>
    // </MainLayout>
  );
}

export default EditProfile;
